import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/lib/auth-store';
import { getJson } from '@/lib/http';
import {
  canEditPhotoChecks,
  canMakePhotoChecksLive,
  canPublishPhotoChecks,
  photoRuleLabel,
  type PhotoChecksMeta,
} from '@/lib/photoChecksAccess';
import { apiRequest } from '@/lib/queryClient';

type SubmissionType = 'POSTING' | 'REMOVAL' | 'REPOST';

type PromptConfig = {
  id: number;
  name: string;
  version: number;
  status: string;
  submissionType: string;
  isActive: boolean;
  model?: string;
};

const SUBMISSION_TYPES: SubmissionType[] = ['POSTING', 'REMOVAL', 'REPOST'];

function ruleStatus(prompts: PromptConfig[]): {
  status: 'Live' | 'Change proposed' | 'Needs attention' | 'Not checking yet';
  live: PromptConfig | null;
  drafts: PromptConfig[];
} {
  const live = prompts.find((p) => p.isActive && p.status === 'ACTIVE') ?? null;
  const drafts = prompts.filter((p) => p.status === 'DRAFT');
  if (live && drafts.length > 0) return { status: 'Change proposed', live, drafts };
  if (live) return { status: 'Live', live, drafts };
  if (drafts.length > 0) return { status: 'Needs attention', live: null, drafts };
  return { status: 'Not checking yet', live: null, drafts: [] };
}

export default function PhotoChecksPage() {
  const { state } = useAuth();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const canEdit = canEditPhotoChecks(state.user);
  const canPublish = canPublishPhotoChecks(state.user);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const metaQ = useQuery({
    queryKey: ['/api/admin/ai-eval/meta'],
    queryFn: () => getJson<PhotoChecksMeta>('/api/admin/ai-eval/meta'),
  });
  const livePublishEnabled = metaQ.data?.livePublishEnabled === true;
  const canMakeLive = canMakePhotoChecksLive(state.user, livePublishEnabled);

  const postingQ = useQuery({
    queryKey: ['/api/admin/ai-triage/prompts', 'POSTING'],
    queryFn: () => getJson<{ ok: true; prompts: PromptConfig[] }>('/api/admin/ai-triage/prompts?submissionType=POSTING'),
  });
  const removalQ = useQuery({
    queryKey: ['/api/admin/ai-triage/prompts', 'REMOVAL'],
    queryFn: () => getJson<{ ok: true; prompts: PromptConfig[] }>('/api/admin/ai-triage/prompts?submissionType=REMOVAL'),
  });
  const repostQ = useQuery({
    queryKey: ['/api/admin/ai-triage/prompts', 'REPOST'],
    queryFn: () => getJson<{ ok: true; prompts: PromptConfig[] }>('/api/admin/ai-triage/prompts?submissionType=REPOST'),
  });

  const promptsByType = useMemo(() => ({
    POSTING: postingQ.data?.prompts ?? [],
    REMOVAL: removalQ.data?.prompts ?? [],
    REPOST: repostQ.data?.prompts ?? [],
  }), [postingQ.data, removalQ.data, repostQ.data]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/admin/ai-triage/prompts'] });
  };

  const proposeMutation = useMutation({
    mutationFn: async (promptId: number) => {
      setBusyId(promptId);
      setMessage(null);
      setErrorMessage(null);
      const res = await apiRequest(`/api/admin/ai-triage/prompts/${promptId}/clone`, 'POST', {});
      return res.json();
    },
    onSuccess: () => {
      setMessage('Proposed change created. Try it safely before making it live.');
      invalidate();
    },
    onError: (err: Error) => setErrorMessage(err.message),
    onSettled: () => setBusyId(null),
  });

  const makeLiveMutation = useMutation({
    mutationFn: async (promptId: number) => {
      setBusyId(promptId);
      setMessage(null);
      setErrorMessage(null);
      const res = await apiRequest(`/api/admin/ai-triage/prompts/${promptId}/publish`, 'POST', {});
      return res.json();
    },
    onSuccess: () => {
      setMessage('Standard is now live. Human approve/reject decisions are unchanged.');
      invalidate();
    },
    onError: (err: Error) => setErrorMessage(err.message),
    onSettled: () => setBusyId(null),
  });

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-3xl">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Photo Checks</p>
            <h1 className="text-2xl font-semibold">What are we checking?</h1>
          </div>
          <Button variant="outline" size="sm" onClick={() => setLocation('/admin/ai')}>
            Home
          </Button>
        </div>
        <p className="text-sm text-muted-foreground max-w-3xl">
          The photo standards REACTOR applies when helping reviewers. Making a standard live never replaces a human decision.
          {!livePublishEnabled && canPublish
            ? ' Live publish is off for now — drafts and trials only.'
            : ''}
        </p>
      </div>

      {message && (
        <Alert>
          <AlertTitle>Done</AlertTitle>
          <AlertDescription>{message}</AlertDescription>
        </Alert>
      )}
      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>Could not complete action</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        {SUBMISSION_TYPES.map((type) => {
          const { status, live, drafts } = ruleStatus(promptsByType[type]);
          const primaryDraft = drafts[0] ?? null;
          return (
            <Card key={type}>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{photoRuleLabel(type)}</CardTitle>
                    <CardDescription>
                      {live
                        ? `Live: ${live.name} (v${live.version}${live.model ? ` · ${live.model}` : ''})`
                        : 'Not checking this submission type yet'}
                      {drafts.length > 0 ? ` · ${drafts.length} proposed change${drafts.length === 1 ? '' : 's'}` : ''}
                      {primaryDraft?.model ? ` · draft model ${primaryDraft.model}` : ''}
                    </CardDescription>
                  </div>
                  <Badge variant={
                    status === 'Live' ? 'success'
                      : status === 'Not checking yet' ? 'destructive'
                        : status === 'Needs attention' ? 'warning'
                          : 'secondary'
                  }
                  >
                    {status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setLocation('/admin/ai-triage/prompts')}>
                  View what we check
                </Button>
                {canEdit && live && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyId === live.id}
                    onClick={() => proposeMutation.mutate(live.id)}
                  >
                    Propose a change
                  </Button>
                )}
                {primaryDraft && (
                  <Button size="sm" variant="outline" onClick={() => setLocation('/admin/ai/results')}>
                    Is it safe to make live?
                  </Button>
                )}
                {canMakeLive && primaryDraft && (
                  <Button
                    size="sm"
                    disabled={busyId === primaryDraft.id}
                    onClick={() => {
                      const ok = window.confirm(
                        'Make this proposed change live?\n\n'
                        + 'This replaces the current live standard for this submission type. '
                        + 'It does not change any human review decisions.',
                      );
                      if (ok) makeLiveMutation.mutate(primaryDraft.id);
                    }}
                  >
                    Make live
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">
        To try a different judgment model (OpenAI vs Gemini), open the standards editor, set the model on a draft, then run a trial.
        Full wording editor and version history live under Advanced → Full standards editor.
      </p>
    </div>
  );
}
